<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    @keydown.esc.stop="cancel"
  >
    <div
      class="bg-surface rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col gap-4 shadow-xl"
      @click.stop
    >
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 class="text-section-title text-content-primary">
            {{ t('slides_editor.components.image_reframer.title') }}
          </h2>
          <p class="text-body-sm text-content-secondary mt-1">
            {{ t('slides_editor.components.image_reframer.description') }}
          </p>
        </div>
        <button
          type="button"
          @click="cancel"
          class="p-1 rounded-full text-content-secondary hover:text-content-primary hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-contrast-medium"
          :aria-label="t('slides_editor.components.image_reframer.cancel')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="flex-1 min-h-0 overflow-hidden bg-surface-subtle rounded-lg flex items-center justify-center">
        <img ref="imgRef" :src="block.image_url" class="block max-w-full" style="display: block;" />
      </div>

      <div class="flex items-center gap-3 px-1">
        <button
          type="button"
          @click="zoomOut"
          :disabled="!cropperReady"
          class="p-2 rounded-full text-content-secondary hover:text-content-primary hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-contrast-medium disabled:opacity-50 disabled:cursor-not-allowed"
          :aria-label="t('slides_editor.components.image_reframer.zoom_out')"
          :title="t('slides_editor.components.image_reframer.zoom_out')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.34-4.34M19 10.5a8.5 8.5 0 1 1-17 0 8.5 8.5 0 0 1 17 0ZM7.5 10.5h6" />
          </svg>
        </button>

        <input
          type="range"
          :min="zoomMin"
          :max="zoomMax"
          :step="zoomStep"
          :value="zoomLevel"
          @input="onSliderInput"
          :disabled="!cropperReady"
          class="flex-1 accent-brand"
          :aria-label="t('slides_editor.components.image_reframer.zoom')"
        />

        <button
          type="button"
          @click="zoomIn"
          :disabled="!cropperReady"
          class="p-2 rounded-full text-content-secondary hover:text-content-primary hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-contrast-medium disabled:opacity-50 disabled:cursor-not-allowed"
          :aria-label="t('slides_editor.components.image_reframer.zoom_in')"
          :title="t('slides_editor.components.image_reframer.zoom_in')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.34-4.34M19 10.5a8.5 8.5 0 1 1-17 0 8.5 8.5 0 0 1 17 0ZM10.5 7.5v6M7.5 10.5h6" />
          </svg>
        </button>
      </div>

      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-1">
          <button
            type="button"
            @click="fitPhoto"
            :disabled="!cropperReady"
            class="text-label text-content-secondary hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-contrast-medium rounded-md px-2 py-1 disabled:opacity-50"
          >
            {{ t('slides_editor.components.image_reframer.fit') }}
          </button>
          <button
            type="button"
            @click="reset"
            :disabled="!cropperReady"
            class="text-label text-content-secondary hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-contrast-medium rounded-md px-2 py-1 disabled:opacity-50"
          >
            {{ t('slides_editor.components.image_reframer.reset') }}
          </button>
        </div>
        <div class="flex gap-2 items-center">
          <button
            type="button"
            @click="cancel"
            class="px-4 py-2 rounded-lg text-label-lg text-content-primary bg-surface-subtle hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-contrast-medium"
          >
            {{ t('slides_editor.components.image_reframer.cancel') }}
          </button>
          <button
            type="button"
            @click="save"
            :disabled="isSaving || !cropperReady"
            class="px-4 py-2 rounded-lg text-label-lg text-content-inverse bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSaving ? t('slides_editor.components.image_reframer.saving') : t('slides_editor.components.image_reframer.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inject, onBeforeUnmount, onMounted, ref } from 'vue'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { useBlockUpdate } from '../../composables/useBlockUpdate'
import { t } from '@/libs/i18n'

const ZOOM_STEP_FACTOR = 1.2

export default {
  name: 'ImageReframer',
  props: {
    block: {
      type: Object,
      required: true
    },
    aspectRatio: {
      type: Number,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isSaving = inject('isSaving')
    const { onBlockUpdateMany } = useBlockUpdate(props.block)

    const imgRef = ref(null)
    const cropperReady = ref(false)
    const zoomLevel = ref(0)
    const zoomMin = ref(0)
    const zoomMax = ref(1)
    const zoomStep = ref(0.01)

    let cropper = null
    let suppressZoomEvent = false

    const isCustomCrop = () =>
      props.block.image_crop_w !== 1.0 || props.block.image_crop_h !== 1.0 ||
      props.block.image_crop_x !== 0 || props.block.image_crop_y !== 0

    const applyInitialCrop = () => {
      if (!cropper || !imgRef.value?.naturalWidth || !isCustomCrop()) return

      const nw = imgRef.value.naturalWidth
      const nh = imgRef.value.naturalHeight
      const cropX = props.block.image_crop_x * nw
      const cropY = props.block.image_crop_y * nh
      const cropW = props.block.image_crop_w * nw

      const box = cropper.getCropBoxData()
      if (!box?.width || !cropW) return

      // Scale the image so the saved crop region (in source pixels) maps to
      // the fixed-size crop box (in canvas pixels), then translate so the
      // top-left of the crop region lands on the top-left of the crop box.
      const ratio = box.width / cropW
      cropper.setCanvasData({
        left: box.left - cropX * ratio,
        top: box.top - cropY * ratio,
        width: nw * ratio,
        height: nh * ratio
      })
    }

    const syncZoomFromCropper = () => {
      if (!cropper) return
      const data = cropper.getImageData()
      if (data?.naturalWidth) {
        zoomLevel.value = data.width / data.naturalWidth
      }
    }

    const initZoomBounds = () => {
      if (!cropper) return
      const img = cropper.getImageData()
      const box = cropper.getCropBoxData()
      if (!img?.naturalWidth || !box?.width) return
      // Min: the whole image fits inside the crop box (letterboxed). The smaller
      // axis ratio is the zoom at which the longer dimension of the image lands
      // exactly inside the box edge.
      const fitRatio = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight)
      const coverRatio = Math.max(box.width / img.naturalWidth, box.height / img.naturalHeight)
      const currentRatio = img.width / img.naturalWidth
      zoomMin.value = fitRatio
      // Allow at least 6× the cover zoom, plus headroom above whatever zoom
      // we just restored.
      zoomMax.value = Math.max(coverRatio * 6, currentRatio * 1.5)
      zoomStep.value = (zoomMax.value - zoomMin.value) / 200
    }

    const initCropper = () => {
      cropper = new Cropper(imgRef.value, {
        aspectRatio: props.aspectRatio,
        viewMode: 0,
        dragMode: 'move',
        cropBoxResizable: false,
        cropBoxMovable: false,
        toggleDragModeOnDblclick: false,
        autoCropArea: 1,
        background: false,
        guides: false,
        center: false,
        responsive: true,
        zoomOnWheel: true,
        zoomOnTouch: true,
        ready () {
          applyInitialCrop()
          initZoomBounds()
          syncZoomFromCropper()
          cropperReady.value = true
        }
      })

      imgRef.value.addEventListener('zoom', () => {
        if (suppressZoomEvent) return
        // Cropper fires `zoom` before applying — wait a tick for state to settle.
        requestAnimationFrame(syncZoomFromCropper)
      })
    }

    onMounted(() => {
      const img = imgRef.value
      if (!img) return
      if (img.complete && img.naturalWidth) {
        initCropper()
      } else {
        img.addEventListener('load', initCropper, { once: true })
      }
      document.addEventListener('keydown', onKeyDown)
    })

    onBeforeUnmount(() => {
      cropper?.destroy()
      cropper = null
      document.removeEventListener('keydown', onKeyDown)
    })

    const onSliderInput = (event) => {
      if (!cropper) return
      const value = parseFloat(event.target.value)
      suppressZoomEvent = true
      cropper.zoomTo(value)
      suppressZoomEvent = false
      zoomLevel.value = value
    }

    const zoomIn = () => {
      if (!cropper) return
      const next = Math.min(zoomLevel.value * ZOOM_STEP_FACTOR, zoomMax.value)
      cropper.zoomTo(next)
      zoomLevel.value = next
    }

    const zoomOut = () => {
      if (!cropper) return
      const next = Math.max(zoomLevel.value / ZOOM_STEP_FACTOR, zoomMin.value)
      cropper.zoomTo(next)
      zoomLevel.value = next
    }

    const save = async () => {
      if (!cropper || !imgRef.value?.naturalWidth) return
      const img = imgRef.value
      const data = cropper.getData({ rounded: false })
      const result = await onBlockUpdateMany({
        image_crop_x: data.x / img.naturalWidth,
        image_crop_y: data.y / img.naturalHeight,
        image_crop_w: data.width / img.naturalWidth,
        image_crop_h: data.height / img.naturalHeight
      })
      if (result) emit('close')
    }

    const fitPhoto = () => {
      if (!cropper) return
      const img = cropper.getImageData()
      const box = cropper.getCropBoxData()
      if (!img?.naturalWidth || !box?.width) return
      const fitRatio = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight)
      suppressZoomEvent = true
      cropper.zoomTo(fitRatio)
      suppressZoomEvent = false
      // After zoom, cropperjs centers the canvas on its container, not on the
      // crop box — re-center so the letterboxed image sits inside the frame.
      requestAnimationFrame(() => {
        if (!cropper) return
        const newImg = cropper.getImageData()
        const newBox = cropper.getCropBoxData()
        cropper.setCanvasData({
          left: newBox.left + (newBox.width - newImg.width) / 2,
          top: newBox.top + (newBox.height - newImg.height) / 2
        })
        zoomLevel.value = newImg.width / newImg.naturalWidth
      })
    }

    const reset = () => {
      cropper?.reset()
      requestAnimationFrame(syncZoomFromCropper)
    }

    const cancel = () => {
      emit('close')
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        cancel()
      }
    }

    return {
      imgRef,
      cropperReady,
      isSaving,
      zoomLevel,
      zoomMin,
      zoomMax,
      zoomStep,
      onSliderInput,
      zoomIn,
      zoomOut,
      save,
      reset,
      fitPhoto,
      cancel,
      t
    }
  }
}
</script>
